import { type MouseEvent, useEffect, useState } from 'react';

import { LoadingButton, type LoadingButtonProps } from '@mui/lab';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';

import { GeneralDialog, type GeneralDialogProps } from './GeneralDialog';

export type ConfirmationDialogProps<T> = GeneralDialogProps & {
    value?: T;
    onConfirm: (value?: T) => void;
    cancelable?: boolean;
    confirmLabel?: string;
    confirmButtonProps?: LoadingButtonProps;
    // If specified along with setConfirming then confirming will be controlled outside of component.
    confirming?: boolean;
    setConfirming?: (boolean) => void;
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const ConfirmationDialog = <T extends any>(props: ConfirmationDialogProps<T>) => {
    const { t } = useTranslation('common');
    const {
        children,
        value,
        onClose,
        onConfirm,
        cancelable = true,
        confirmLabel,
        confirmButtonProps,
        open,
        confirming: manualConfirming,
        setConfirming: setManualConfirming,
        ...other
    } = props;

    // Automatically set confirming status on "confirm" if 'confirming' wasn't passed in as a property.
    const autoConfirm = manualConfirming === undefined;

    const [autoConfirming, setAutoConfirming] = useState<boolean>(false);

    const confirming = manualConfirming !== undefined ? manualConfirming : autoConfirming;
    const setConfirming = setManualConfirming !== undefined ? setManualConfirming : setAutoConfirming;

    const label = confirmLabel ?? t('confirm');

    useEffect(() => {
        // If the modal is opened or closed then the "confirming" state must be reset.
        setConfirming(false);
    }, [open, setConfirming]);

    const handleConfirm = () => {
        if (autoConfirm) {
            setConfirming(true);
        }
        onConfirm(value);
    };

    const handleClose = (event: MouseEvent<HTMLButtonElement>, reason: 'backdropClick' | 'escapeKeyDown') => {
        setConfirming(false);
        if (typeof onClose === 'function') {
            onClose(event, reason);
        }
    };

    return (
        <GeneralDialog
            onClose={handleClose}
            open={open}
            {...other}
            actions={
                <>
                    {cancelable && (
                        <Button
                            autoFocus
                            onClick={(e) => handleClose(e, 'backdropClick')}
                            disabled={confirming}
                            variant="outlined"
                        >
                            {t('cancel')}
                        </Button>
                    )}
                    <LoadingButton
                        onClick={handleConfirm}
                        disabled={confirming}
                        loading={confirming}
                        aria-busy={confirming}
                        variant={confirming ? 'outlined' : 'contained'}
                        color="primary"
                        {...confirmButtonProps}
                    >
                        {label}
                    </LoadingButton>
                </>
            }
        >
            {children}
        </GeneralDialog>
    );
};
